import { useEffect } from "react";
import { AdminCommandBar, ErrorBoundary } from "@/components";
import { AppSidebar } from "@/components/app-sidebar";
import { BrandAsset } from "@/components/branding/BrandAsset";
import { SessionTimeoutWarning } from "@/components/session/SessionTimeoutWarning";
import { SidebarProvider, useSidebar } from "@/components/sidebar";
import { SideSheetStackProvider } from "@/components/ui/SideSheet";
import { useCreditThresholdToasts } from "@/hooks/use-credit-threshold-toasts";
import { useCreditExhaustionWatcher } from "@/hooks/use-credit-exhaustion-watcher";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { useSignOut } from "@/packages/features-auth";
import { useTranslation } from "@/packages/utils";
import { getAuthDeepLinkState } from "@/routes/authDeepLink";
import { AuthPage } from "@/routes/auth";
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Authenticated, Unauthenticated, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { Menu } from "lucide-react";
import { Suspense } from "react";

function MobileNavToggle() {
  const { t } = useTranslation(["sidebar"]);
  const { isMobile, collapsed, toggleCollapsed } = useSidebar();

  if (!isMobile || !collapsed) return null;

  return (
    <button
      type="button"
      onClick={toggleCollapsed}
      title={t("sidebar:actions.openSidebarTitle")}
      className="fixed top-4 left-4 z-30 flex h-9 items-center gap-2 rounded-full border border-border/60 bg-surface px-2.5 text-muted shadow-sm hover:bg-card-hover/50 hover:text-secondary transition-all duration-150"
    >
      <BrandAsset alt="Audinero" className="size-4 opacity-90" variant="icon" />
      <Menu size={16} strokeWidth={2} />
    </button>
  );
}

/**
 * Onboarding guard: redirects users based on workspace membership.
 * - No workspace + not on /onboarding -> redirect to /onboarding
 * - Has workspace + on / -> redirect to /{first-slug}/dashboard
 * - Has workspace + on /onboarding -> redirect to /{first-slug}/dashboard
 */
function WorkspaceGuard() {
  const hasWorkspace = useQuery(api.workspaces.hasWorkspace, {});
  const myWorkspaces = useQuery(api.workspaces.getMyWorkspaces, {});
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });

  // biome-ignore lint/correctness/useExhaustiveDependencies: location.search.new intentionally omitted — only pathname changes should trigger redirect
  useEffect(() => {
    if (hasWorkspace === undefined || myWorkspaces === undefined) return;

    const path = location.pathname;

    // Paths that should not trigger workspace guards
    const isExemptPath =
      path.startsWith("/dev") ||
      path.startsWith("/auth") ||
      path.startsWith("/invite");
    if (isExemptPath) return;

    if (!hasWorkspace && path !== "/onboarding") {
      navigate({ to: "/onboarding" });
      return;
    }

    if (!hasWorkspace) return;

    const defaultSlug = myWorkspaces[0]?.slug;
    if (!defaultSlug) return;

    // Redirect from root or onboarding to user's own workspace
    // Allow /onboarding?new=true for explicit "create workspace" navigation
    const isExplicitOnboarding =
      path === "/onboarding" &&
      (location.search as Record<string, unknown>).new === true;
    if (isExplicitOnboarding) return;

    if (path === "/" || path === "/onboarding") {
      navigate({
        to: "/$workspaceSlug/dashboard",
        params: { workspaceSlug: defaultSlug },
      });
      return;
    }

    // Redirect if on a workspace that doesn't belong to this user
    const currentSlug = path.split("/")[1];
    const mySlugs = new Set(myWorkspaces.map((ws) => ws.slug));
    if (currentSlug && !mySlugs.has(currentSlug)) {
      navigate({
        to: "/$workspaceSlug/dashboard",
        params: { workspaceSlug: defaultSlug },
      });
    }
  }, [hasWorkspace, myWorkspaces, location.pathname, navigate]);

  return null;
}

function AuthenticatedLayout() {
  const location = useRouterState({ select: (s) => s.location });
  const { signOut } = useSignOut();

  const { showWarning, secondsRemaining, extendSession } = useIdleTimeout({
    onTimeout: signOut,
  });

  // Phase 5: show toast notifications when AI credit thresholds are crossed
  useCreditThresholdToasts();

  // Watch for async AI action credit exhaustion and open modal
  useCreditExhaustionWatcher();

  // Onboarding has its own full-screen layout — render without app chrome
  if (location.pathname === "/onboarding") {
    return (
      <SideSheetStackProvider>
        <WorkspaceGuard />
        <SessionTimeoutWarning
          open={showWarning}
          secondsRemaining={secondsRemaining}
          onExtend={extendSession}
          onLogout={signOut}
        />
        <Outlet />
      </SideSheetStackProvider>
    );
  }

  return (
    <SidebarProvider>
      <SideSheetStackProvider>
        <WorkspaceGuard />
        <SessionTimeoutWarning
          open={showWarning}
          secondsRemaining={secondsRemaining}
          onExtend={extendSession}
          onLogout={signOut}
        />
        <div className="flex h-screen overflow-hidden bg-page">
          <AppSidebar />
          <MobileNavToggle />

          <main className="flex-1 overflow-hidden overflow-y-auto px-4 py-1 pt-2">
            <Outlet />
          </main>
        </div>
      </SideSheetStackProvider>
    </SidebarProvider>
  );
}

/**
 * Unauthenticated handler: renders the Outlet for /invite/* paths
 * (invite page handles its own redirect to /auth), otherwise shows AuthPage.
 */
function UnauthenticatedHandler() {
  const location = useRouterState({ select: (s) => s.location });
  const authDeepLink = getAuthDeepLinkState();

  if (
    location.pathname.startsWith("/invite") ||
    location.pathname.startsWith("/auth")
  ) {
    return <Outlet />;
  }

  return (
    <AuthPage
      initialFlow={authDeepLink.flow}
      initialEmail={authDeepLink.email}
      initialResetCode={
        authDeepLink.flow === "reset-otp" ? authDeepLink.code : undefined
      }
      initialOtpCode={
        authDeepLink.flow === "otp-verification" ? authDeepLink.code : undefined
      }
    />
  );
}

export const Route = createRootRoute({
  component: () => (
    <ErrorBoundary>
      <div>
        <Authenticated>
          <AuthenticatedLayout />
        </Authenticated>

        <Unauthenticated>
          <UnauthenticatedHandler />
        </Unauthenticated>

        {/* Admin command bar (Cmd+J) - development only */}
        {import.meta.env.DEV && (
          <Suspense fallback={null}>
            <AdminCommandBar />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  ),
});
